import React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, fmtMoney, BRAND } from "../lib/api";
import { Printer } from "lucide-react";

export default function Invoice() {
  const { invoiceNumber } = useParams();
  const { data: inv, isLoading, error } = useQuery({
    queryKey: ["invoice", invoiceNumber],
    queryFn: async () => (await api.get(`/admin/invoices/${invoiceNumber}`)).data,
  });

  if (isLoading) return <div className="section-container py-24 text-slate-400">Loading…</div>;
  if (error || !inv) return <div className="section-container py-24 text-slate-400">Invoice not found. (Admin login required)</div>;

  return (
    <div className="section-container py-16 max-w-3xl">
      <div className="flex justify-end mb-6 print:hidden">
        <button onClick={()=>window.print()} className="btn-outline-gold" data-testid="invoice-print-btn"><Printer size={14} className="inline mr-2"/>Print / Save PDF</button>
      </div>

      <div className="lume-card p-10 sm:p-14 bg-white/[0.02]" data-testid="invoice-doc">
        <div className="flex items-start justify-between border-b border-white/10 pb-6">
          <div>
            <div className="font-serif text-3xl text-[color:var(--lume-gold-light)]">LUME <span className="text-[color:var(--lume-gold)]">·</span> Enterprise</div>
            <div className="text-xs uppercase tracking-[0.25em] text-slate-400 mt-2">Private Chauffeured Transportation</div>
            <div className="mt-4 text-xs text-slate-400">{BRAND.phone} · {BRAND.email}</div>
          </div>
          <div className="text-right">
            <div className="eyebrow">Invoice</div>
            <div className="font-mono text-lg text-white mt-1">{inv.invoice_number}</div>
            <div className="text-xs text-slate-400 mt-2">Issued {new Date(inv.created_at).toLocaleDateString()}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8">
          <div>
            <div className="eyebrow mb-2">Billed To</div>
            <div className="text-white">{inv.customer_name}</div>
            <div className="text-sm text-slate-400">{inv.customer_email}</div>
            <div className="text-sm text-slate-400">{inv.customer_phone}</div>
          </div>
          <div>
            <div className="eyebrow mb-2">{inv.kind === "corporate_monthly" ? "Billing Period" : "Reservation"}</div>
            {inv.kind === "corporate_monthly" ? (
              <>
                <div className="text-white font-mono">{inv.month}</div>
                <div className="text-sm text-slate-400">{inv.company_name}</div>
                <div className="text-sm text-slate-400">{inv.items?.length || 0} completed rides</div>
              </>
            ) : (
              <>
                <div className="text-white font-mono">{inv.reservation_number}</div>
                <div className="text-sm text-slate-400 capitalize">{(inv.service_type || "").replace(/-/g," ")}</div>
                <div className="text-sm text-slate-400">{inv.vehicle_class_name}</div>
              </>
            )}
          </div>
        </div>

        <div className="mt-8 border border-white/10 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.02]">
              <tr className="text-left text-xs uppercase tracking-widest text-slate-400">
                <th className="p-3">Description</th><th className="p-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {inv.items ? inv.items.map((it, i) => (
                <tr key={i} className="border-t border-white/5">
                  <td className="p-3">
                    <div className="text-white">{it.rider} — {it.service}</div>
                    <div className="text-xs text-slate-500 mt-1">{it.date} · {it.reservation_number} · {it.vehicle}</div>
                  </td>
                  <td className="p-3 text-right text-white">{fmtMoney(it.amount)}</td>
                </tr>
              )) : (
                <tr className="border-t border-white/5">
                  <td className="p-3">
                    <div className="text-white">Chauffeured Transportation — {inv.vehicle_class_name}</div>
                    <div className="text-xs text-slate-500 mt-1">{inv.pickup_date} at {inv.pickup_time} · {inv.pickup_location} → {inv.destination || "As directed"}</div>
                  </td>
                  <td className="p-3 text-right text-white">{fmtMoney(inv.base_fare)}</td>
                </tr>
              )}
              {inv.comfort_fee > 0 && (
                <tr className="border-t border-white/5">
                  <td className="p-3"><div className="text-white">Comfort Package</div><div className="text-xs text-slate-500 mt-1">Bottled water — Complimentary</div></td>
                  <td className="p-3 text-right text-white">{fmtMoney(inv.comfort_fee)}</td>
                </tr>
              )}
              {inv.referral_credit > 0 && (
                <tr className="border-t border-white/5">
                  <td className="p-3"><div className="text-white">Referral Discount Applied</div></td>
                  <td className="p-3 text-right text-emerald-400">− {fmtMoney(inv.referral_credit)}</td>
                </tr>
              )}
              {inv.sales_tax > 0 && (
                <tr className="border-t border-white/5">
                  <td className="p-3"><div className="text-white">Sales Tax (8.875%)</div></td>
                  <td className="p-3 text-right text-white">{fmtMoney(inv.sales_tax)}</td>
                </tr>
              )}
              {inv.waiting_charge > 0 && (
                <tr className="border-t border-white/5">
                  <td className="p-3"><div className="text-white">Waiting Time</div><div className="text-xs text-slate-500 mt-1">{inv.waiting_minutes} min total · {inv.grace_minutes} min grace included · $1/min after</div></td>
                  <td className="p-3 text-right text-white">{fmtMoney(inv.waiting_charge)}</td>
                </tr>
              )}
              {inv.additional_charges > 0 && (
                <tr className="border-t border-white/5">
                  <td className="p-3"><div className="text-white">Additional Charges</div><div className="text-xs text-slate-500 mt-1">{inv.charges_description}</div></td>
                  <td className="p-3 text-right text-white">{fmtMoney(inv.additional_charges)}</td>
                </tr>
              )}
              {inv.referral_credit > 0 && (
                <tr className="border-t border-white/5">
                  <td className="p-3"><div className="text-white">Referral Credit Applied</div></td>
                  <td className="p-3 text-right text-emerald-400">− {fmtMoney(inv.referral_credit)}</td>
                </tr>
              )}
              <tr className="border-t border-white/5">
                <td className="p-3"><div className="text-slate-300">Tolls</div><div className="text-xs text-slate-500 mt-1">Tolls are separate and may apply depending on the route.</div></td>
                <td className="p-3 text-right text-slate-400">Separate</td>
              </tr>
            </tbody>
            <tfoot>
              <tr className="border-t border-[color:var(--lume-gold)]/30">
                <td className="p-4 text-xs uppercase tracking-widest text-slate-400">Total</td>
                <td className="p-4 text-right font-serif text-2xl text-[color:var(--lume-gold-light)]" data-testid="invoice-total">{fmtMoney(inv.total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <div className="eyebrow">Payment Status</div>
          <span className={`status-pill status-${inv.payment_status === 'paid' ? 'completed' : 'pending'}`}>{inv.payment_status}</span>
        </div>

        <div className="mt-10 pt-6 border-t border-white/10 text-xs text-slate-500 text-center">
          Thank you for choosing Lume Enterprise. We never ask for password or full card details by email.
        </div>
      </div>
    </div>
  );
}
